import type { ScriptShot } from '@/api/capture';
import { useT } from '@/i18n/runtime';
import { shotText } from '@/i18n/serverText';
import type { RefusedShot } from '@/store/captureStore';
import type { ReactNode } from 'react';
import { familyDemoFor } from './demos/demoTypes';
import { GuidanceControls } from './components/GuidanceControls';
import { Icon } from './components/Icon';
import { OrientationHint } from './components/OrientationHint';

/** F-S7 — the be runs no Aadhaar mask on this step (a Gulf / tenant-pack case): it says `NOT_APPLICABLE_POLICY`. */
export function maskingNotApplicable(shot: Pick<ScriptShot, 'masking'> | null | undefined): boolean {
  return shot?.masking === 'NOT_APPLICABLE_POLICY';
}

/**
 * US-03482 §14f — a refusal says WHY in words the customer can act on. An ID-document photo the server's redactor could
 * not vouch for, or a photo it could not open, is a problem with the PHOTO: take a new one (the refused bytes are never
 * sent again). An empty clip the server would not seal (`SV_CLIP_EMPTY`) says nothing was recorded. Every other refusal
 * (the recording's chain did not verify) keeps the recording words. F-S7: on a step the be does not mask, the document
 * refusal names no India document list.
 */
export function refusalKeys(
  code: string,
  shot?: Pick<ScriptShot, 'masking'> | null,
): { readonly title: string; readonly body: string } {
  switch (code) {
    case 'SV_DOCUMENT_NOT_RECOGNISED':
      return {
        title: 'sv.capture.refused.doc.title',
        body: maskingNotApplicable(shot) ? 'sv.capture.refused.doc.bodyPolicy' : 'sv.capture.refused.doc.body',
      };
    case 'SV_IMAGE_TOO_LARGE':
    case 'SV_IMAGE_UNDECODABLE':
    case 'SV_DOCUMENT_NOT_ONE_CHUNK':
      return { title: 'sv.capture.refused.photo.title', body: 'sv.capture.refused.photo.body' };
    case 'SV_CLIP_EMPTY':
      // be G5: the same words the app's own empty-recording guard says — record the step again
      return { title: 'sv.capture.refused.title', body: 'sv.gate.clip.empty' };
    default:
      return { title: 'sv.capture.refused.title', body: 'sv.capture.refused.body' };
  }
}

/**
 * Everything a shot says about itself, as the compact chrome over the top of the viewfinder
 * (SPEC_CAPTURE_UI_20260925 §2): the progress strip (n of N, current highlighted) with the
 * viewfinder pills on its right, then the instruction card — step / kind / mandatory eyebrow, the
 * SERVER's prompt text verbatim in the chosen language (`shotText`, FSD-18 D1), orientation and duration on one
 * line — and, when the server refused
 * a previous attempt, the honest refusal card with its reason code.
 *
 * Renders the same DOM (and test ids) inside the blocked-camera column; CSS restyles it there.
 */
export function ShotHeader({
  shot,
  stepIndex,
  stepTotal,
  refusal,
  recaptureOnly,
  right,
  onSkip = null,
  folded = false,
}: {
  shot: ScriptShot;
  stepIndex: number;
  stepTotal: number;
  refusal: RefusedShot | null;
  recaptureOnly: boolean;
  /** The pill cluster (camera facing, rec timer, grid toggle) rendered on the strip's right. */
  right?: ReactNode;
  /**
   * FSD-20 Addendum C: an OPTIONAL step's "Skip", beside the word "Optional" it acts on — the bar's right slot is the
   * language switcher now. The caller passes it only when the bar used to show it (camera live, nothing to decide).
   */
  onSkip?: (() => void) | null;
  /** While a clip records the card folds to its step line and words; the rest comes back when it stops. */
  folded?: boolean;
}): JSX.Element {
  const { t, locale } = useT();
  const prompt = shotText(shot, locale);
  const steps = Array.from({ length: Math.max(stepTotal, 1) }, (_, i) => i + 1);
  return (
    <div className="sv-instruct-wrap" data-testid="shot-header" data-shot-id={shot.shotId} data-kind={shot.kind}>
      <div className="sv-vf-row">
        <div className="sv-progress" data-testid="shot-progress" data-step={stepIndex + 1} data-total={stepTotal} aria-hidden="true">
          {steps.map((n) => (
            <i key={`step-${n}`} className={n - 1 < stepIndex ? 'is-done' : n - 1 === stepIndex ? 'is-current' : ''} />
          ))}
        </div>
        {right ?? null}
      </div>
      <div className={folded ? 'sv-instruct is-folded' : 'sv-instruct'} data-folded={folded ? 'true' : 'false'}>
        <div className="sv-instruct-head">
          <div className="sv-shot-meta mono">
            <span>{t('sv.capture.progress', { n: String(stepIndex + 1), total: String(stepTotal) })}</span>
            <span>·</span>
            <span>{shot.kind === 'CLIP' ? t('sv.capture.clip') : t('sv.capture.still')}</span>
            <span>·</span>
            <span className={shot.mandatory ? 'sv-req is-req' : 'sv-req'} data-testid="shot-mandatory">
              {shot.mandatory ? t('sv.capture.mandatory') : t('sv.capture.optional')}
            </span>
          </div>
          {onSkip && !shot.mandatory ? (
            <button type="button" className="sv-skip-chip" aria-label={t('sv.action.skipShot')} onClick={onSkip} data-testid="skip-shot">
              <Icon name="skip" size={14} />
              {t('sv.action.skipShort')}
            </button>
          ) : null}
        </div>
        <div className="sv-prompt" data-testid="shot-prompt" lang={prompt.locale} dir="auto">{prompt.text}</div>
        {/* FSD-20 D6: orientation / duration and the guidance controls share ONE line (it wraps when it must), so the
            card spends no extra row on them over the video. */}
        <div className="sv-instruct-foot">
          <div className="sv-instruct-meta">
            <OrientationHint required={shot.orientation} />
            {shot.kind === 'CLIP' ? (
              <span className="sv-duration" data-testid="shot-duration">
                {shot.maxDurationS > 0
                  ? t('sv.capture.duration', { min: String(shot.minDurationS), max: String(shot.maxDurationS) })
                  : t('sv.capture.durationMin', { min: String(shot.minDurationS) })}
              </span>
            ) : null}
          </div>
          {/* §3.1: the replay + mute controls live in the instruction card, beside the words they speak; §4: so does
              "Show me". The card is also rendered inside the blocked-camera column, so the controls exist there too. */}
          <GuidanceControls demoType={familyDemoFor(shot.promptKey)} />
        </div>
        {recaptureOnly ? <p className="sv-instruct-note" role="note">{t('sv.capture.recaptureOnly')}</p> : null}
      </div>
      {refusal ? (
        <div className="sv-instruct sv-refused" role="alert" data-testid="shot-refused" data-code={refusal.code}>
          <div className="pt"><Icon name="info" size={14} /> {t(refusalKeys(refusal.code, shot).title)}</div>
          <div className="pd">{t(refusalKeys(refusal.code, shot).body)}</div>
          <div className="mono sv-instruct-note">{t('sv.capture.refused.code', { code: refusal.code })}</div>
        </div>
      ) : null}
    </div>
  );
}
